// Dagtype van een datum of periode wijzigen (spec R1 AC2/AC3, R3, R4): eerst zien wat er verandert, dan één keer
// bevestigen; 10 seconden ongedaan te maken. Herhalende afspraken in een vakantie krijgen daarna "Laten vervallen?".
import { useState, useSyncExternalStore } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import type { Occurrence } from "@shared/recurrence";
import { weekTarget } from "@shared/routines";
import { addDays, displayDate, isoWeekday, planningDate } from "@shared/time";
import { skipOccurrence, useOccurrences } from "../lib/appointments";
import { db } from "../lib/db";
import { clearDayType, isPausedIn, overrideOn, setDayType, standardTemplate, useRhythm } from "../lib/rhythm";
import { useRoutines } from "../lib/routines";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

let state: string | null = null;
const listeners = new Set<() => void>();
export function openDayType(date: string | null) {
  state = date;
  listeners.forEach((l) => l());
}
function useOpen() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

const NORMAL = "normal";
const SEED_ORDER = ["ziek", "vakantie", "vrij"];

export function DayTypeSheetHost() {
  const date = useOpen();
  if (!date) return null;
  return <DayTypeSheet key={date} date={date} />;
}

function DayTypeSheet({ date }: { date: string }) {
  const rhythm = useRhythm();
  const routines = useRoutines();
  const today = planningDate(new Date());
  const current = rhythm ? overrideOn(rhythm, date) : undefined;
  const [choice, setChoice] = useState<string | null>(null);
  const [until, setUntil] = useState<string | null>(null);
  const [series, setSeries] = useState<Occurrence[] | null>(null);
  const picked = choice ?? current?.template_id ?? NORMAL;
  const shownUntil = until ?? (picked === NORMAL && current ? current.ends_on : date);
  const to = shownUntil < date ? date : shownUntil;
  const occurrences = useOccurrences(date, to);
  const tasks = useLiveQuery(
    () =>
      db.plan_entries
        .where("plan_date")
        .between(date, to, true, true)
        .filter((e) => e.kind === "task" && e.status === "planned" && !e.deleted_at)
        .count(),
    [date, to],
  );
  const close = () => openDayType(null);
  if (!rhythm) return null;

  const standard = standardTemplate(rhythm, date);
  const selected = picked;
  const template = rhythm.templates.find((t) => t.id === selected);
  const ordered = [...rhythm.templates].sort((a, b) => {
    const ia = SEED_ORDER.indexOf(a.seed_key ?? "");
    const ib = SEED_ORDER.indexOf(b.seed_key ?? "");
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.position - b.position;
  });
  const past = date < today;
  const pausing = !!template?.pauses_routines;
  const holiday = template?.seed_key === "vakantie";

  // What changes (R4 AC2): counted on this device; the server does it.
  const wasPaused = isPausedIn(rhythm);
  const willPause = (d: string) => (d >= date && d <= to ? pausing : wasPaused(d));
  const counted = (routines ?? []).filter((r) => r.weekly_target);
  // One date per week in the range: the first day, then every Monday.
  const span = Math.min(93, Math.round((Date.parse(to) - Date.parse(date)) / 86_400_000) + 1);
  const weeks = Array.from({ length: span }, (_, i) => addDays(date, i)).filter((d, i) => i === 0 || isoWeekday(d) === 1);
  const lower = counted.flatMap((r) =>
    weeks
      .map((d) => ({ before: weekTarget(r, d, wasPaused), after: weekTarget(r, d, willPause) }))
      .filter((w) => w.after < w.before)
      .slice(0, 1)
      .map((w) => `${r.title} telt ${weeks.length > 1 ? "in de eerste week" : "deze week"} tot ${w.after} in plaats van ${w.before}.`),
  );
  const repeating = (occurrences ?? []).filter((o) => o.repeats);

  async function confirm() {
    if (past) return;
    if (selected === NORMAL) {
      await clearDayType(date, to);
      toast("Terug naar het gewone dagtype");
      close();
      return;
    }
    const id = await setDayType(date, to, selected);
    const label =
      template?.seed_key === "ziek"
        ? "Ziek gemeld"
        : template?.seed_key === "vakantie"
          ? "Vakantie ingesteld"
          : `${template?.name ?? "Dagtype"} ingesteld`;
    toast(label, { duration: 10_000, action: { label: "Ongedaan maken", onClick: () => void clearDayType(date, to, id) } });
    if (holiday && repeating.length) setSeries(repeating);
    else close();
  }

  if (series) {
    const byAppointment = new Map<string, Occurrence[]>();
    for (const o of series) byAppointment.set(o.appointmentId, [...(byAppointment.get(o.appointmentId) ?? []), o]);
    return (
      <Sheet
        open
        onOpenChange={(o) => !o && close()}
        title="Herhalende afspraken"
        description="Deze afspraken vallen in die dagen. Ze blijven staan, tenzij je ze laat vervallen."
      >
        <ul className="flex flex-col gap-3">
          {[...byAppointment].map(([appointmentId, list]) => (
            <li key={appointmentId} className="rounded-2xl border border-mist bg-paper p-3">
              <p className="font-semibold [overflow-wrap:anywhere]">{list[0]!.title}</p>
              <p className="mb-2 text-sm text-ink-soft">{list.map((o) => displayDate(o.date)).join(", ")}</p>
              <Button
                variant="secondary"
                className="w-full"
                onClick={async () => {
                  for (const o of list) await skipOccurrence(o);
                  toast(`${list[0]!.title} vervalt ${list.length === 1 ? "die dag" : `${list.length} keer`}`);
                  setSeries((s) => (s ?? []).filter((o) => o.appointmentId !== appointmentId));
                }}
              >
                Laten vervallen
              </Button>
            </li>
          ))}
        </ul>
        <Button variant="quiet" className="mt-3 w-full" onClick={close}>
          {byAppointment.size ? "Laten staan" : "Klaar"}
        </Button>
      </Sheet>
    );
  }

  const options = [
    { id: NORMAL, name: `Normaal${standard ? ` (${standard.name})` : ""}` },
    ...ordered.map((t) => ({ id: t.id, name: t.name })),
  ];
  return (
    <Sheet open onOpenChange={(o) => !o && close()} title="Dagtype" description={displayDate(date)}>
      {past ? (
        <p className="text-ink-soft">Een dag in het verleden verandert niet meer.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <fieldset>
            <legend className="mb-1 font-semibold">Wat voor dag</legend>
            <div className="flex flex-col gap-1" role="radiogroup" aria-label="Dagtype">
              {options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={selected === o.id}
                  onClick={() => setChoice(o.id)}
                  className={cn(
                    "flex min-h-12 items-center rounded-xl border-2 px-3 text-left",
                    selected === o.id ? "border-pine bg-pine text-paper" : "border-mist",
                  )}
                >
                  {o.name}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="flex flex-col gap-1">
            <span className="font-semibold">Tot en met</span>
            <input
              type="date"
              min={date}
              value={shownUntil}
              onChange={(e) => setUntil(e.target.value || date)}
              className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
            />
          </label>
          {pausing && (
            <section aria-labelledby="gevolgen" className="rounded-2xl border border-mist bg-paper p-3">
              <h3 id="gevolgen" className="mb-1 font-semibold">
                Wat er verandert
              </h3>
              <ul className="list-disc pl-5 text-ink-soft">
                {!!tasks && <li>{tasks === 1 ? "1 taak gaat" : `${tasks} taken gaan`} terug naar Te doen.</li>}
                <li>Routines vervallen op die dagen en komen niet in Nog deze week.</li>
                {lower.map((l) => (
                  <li key={l}>{l}</li>
                ))}
                <li>Geen meldingen voor blokken; afspraken houden hun melding.</li>
                {holiday && repeating.length > 0 && <li>Daarna vraag ik of je herhalende afspraken in die dagen wilt laten vervallen.</li>}
                {!holiday && (occurrences ?? []).length > 0 && (
                  <li>Afspraken blijven staan; bij elke afspraak vraag ik of je hem wilt afzeggen.</li>
                )}
              </ul>
            </section>
          )}
          <Button onClick={() => void confirm()}>
            {selected === NORMAL
              ? "Opslaan"
              : template?.seed_key === "ziek"
                ? "Ziek melden"
                : template?.seed_key === "vakantie"
                  ? "Vakantie instellen"
                  : "Opslaan"}
          </Button>
        </div>
      )}
    </Sheet>
  );
}
